import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import * as yup from 'yup';
import { DateTime } from 'luxon';
import AccordionEndPracticeForm from './form';
import { useParams } from 'react-router-dom';
import { EndPracticeVM } from 'practice/view-models/practice-detail';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';

interface EndPracticeProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
}

const AccordionEndPracticeComponent = ({
  loadUpdateDate,
}: EndPracticeProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const updateData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/sospensione-fine-pratica`, {
    options: { method: 'POST' },
  });

  const params = useParams();

  const onSubmit = useEventCallback((values: EndPracticeVM) => {
    const dateEnd = values.endSuspensionDate?.toFormat('yyyy-MM-dd') || '';
    const hoursEnd = values.timeEnd?.toFormat('THHmmssSSS') || '';
    const timestampInizioSospensione =
      DateTime.fromFormat(`${dateEnd || ''} ${hoursEnd || ''}`, 'yyyy-MM-dd THHmmssSSS') ||
      undefined;
    const dateStart = values.startSuspensionDate?.toFormat('yyyy-MM-dd') || '';
    const hoursStart = values.timeStart?.toFormat('THHmmssSSS') || '';
    const timestampFineSospensione =
      DateTime.fromFormat(`${dateStart || ''} ${hoursStart || ''}`, 'yyyy-MM-dd THHmmssSSS') ||
      undefined;

    (
      updateData({
        data: {
          key: {
            codiceEnte: params?.codiceEnte || undefined,
            tipoRegistro: params?.tipoRegistro || undefined,
            idAnnoPratica: params?.idAnnoPratica
              ? Number.parseInt(params?.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params?.progressivoPratica
              ? Number.parseInt(params?.progressivoPratica, 10)
              : undefined,
          },
          tipoPratica: params?.tipoPratica || undefined,
          sospensioneVO: {
            timestampInizioSospensione,
            timestampFineSospensione,
            noteSospensione: values.reason || undefined,
          },
        },
      }) as ResponsePromise<void>
    ).then(async () => {
      toast.success('Operazione eseguita con successo');

      // aggiornamento stato pratica
      formContextPage.setFieldValue('practice', {
        ...(formContextPage.values.practice || {}),
        codiceStatoPratica: '1',
        tipoOperazione: '59',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }
    });
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormik<EndPracticeVM>({
    initialValues: formContextPage.values.suspension || {
      startSuspensionDate: null,
      timeStart: null,
      endSuspensionDate: DateTime.now(),
      timeEnd: DateTime.now(),
      reason: '',
    },
    validationSchema: yup.object().shape({
      endSuspensionDate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      timeEnd: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      reason: yup.string().nullable().required('Il campo è obbligatorio'),
    }),
    onSubmit,
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-end-practice-content"
          id="panel-end-practice-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Registra Fine Sospensione Pratica</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionEndPracticeForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            errors={formContext.errors}
            touched={formContext.touched}
          />
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => formContext.handleSubmit()}
              id="confirm"
            >
              Conferma
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionEndPracticeComponent.displayName = 'Accordion End Practice component';

const AccordionEndPractice = memo(AccordionEndPracticeComponent);

export default AccordionEndPractice;
